export interface ProductResponse {
  id: number;
  sku: string;
  name: string;
  attribute_set_id: number;
  price: number;
  status: number;
  visibility: number;
  type_id: string;
  created_at: string;
  updated_at: string;
  extension_attributes: ExtensionAttributes;
  product_links: any[];
  options: any[];
  media_gallery_entries: MediaGalleryEntry[];
  tier_prices: any[];
  custom_attributes: Record<string, string>; // Map of attribute_code to value
}

interface ExtensionAttributes {
  website_ids: number[];
  configurable_product_options: any[];
  configurable_product_links: any[];
}

interface MediaGalleryEntry {
  id: number;
  media_type: string;
  label: string | null;
  position: number;
  disabled: boolean;
  types: string[];
  file: string;
}

interface CustomAttribute {
  attribute_code: string;
  value: string;
}

/**
 * Get info about a product by SKU.
 *
 * Sends a GET request to the `/rest/V1/products/${sku}` endpoint.
 *
 * @param sku A string representing the product SKU.
 * @returns A promise that resolves to an array of product data for the specified SKU.
 * @throws An error if the fetch request fails or the response is not OK.
 */
export const fetchProductBySku = async (
  sku?: string | null,
): Promise<ProductResponse> => {
  try {
    if (sku) {
      const response = await fetch(`/rest/V1/products/${sku}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: ProductResponse & {
        custom_attributes: CustomAttribute[];
      } = await response.json();

      // Convert the array to a map of key/value pairs
      const customAttributeMap = data.custom_attributes.reduce(
        (acc, attribute) => {
          acc[attribute.attribute_code] = attribute.value;
          return acc;
        },
        // TODO: refactor without type assertion, or add comment
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        {} as Record<string, string>,
      );

      return {
        ...data,
        custom_attributes: customAttributeMap,
      };
    }
    // TODO: refactor without type assertion, or add comment
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {} as ProductResponse;
  } catch (error) {
    console.error('Error fetching distributors:', error);
    throw error;
  }
};
